import React, { useState, useEffect } from 'react';
import { CompetitionClassRead, CompetitionResultRead, Country } from '../types/api';
import { apiService } from '../services/api';
import { urlParams } from '../utils/urlParams';
import { getCircleFlagUrl, resolveCountryName } from '../utils/countryFlag';
import { getHorseAvatarUrl } from '../utils/horseAvatar';
import { formatPersonName } from '../utils/personName';
import { formatHorseName } from '../utils/horseName';
import { MapPin, Calendar, Ruler, Users, Trophy, Clock, AlertTriangle, ExternalLink } from 'lucide-react';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatTime(seconds: number | null): string {
  if (seconds == null) return '';
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(2);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function formatPrize(prize: number | null, currency: string | null): string {
  if (prize == null) return '';
  const sym = { EUR: '€', USD: '$', GBP: '£', CHF: 'CHF ' }[currency?.toUpperCase() || ''] || (currency ? `${currency} ` : '');
  return `${sym}${prize.toLocaleString()}`;
}

function getRankStyle(rank: number | null): { bg: string; text: string } {
  if (rank === 1) return { bg: 'bg-amber-100', text: 'text-amber-700' };
  if (rank === 2) return { bg: 'bg-gray-200', text: 'text-gray-700' };
  if (rank === 3) return { bg: 'bg-orange-100', text: 'text-orange-700' };
  return { bg: 'bg-gray-100', text: 'text-gray-600' };
}

interface CompetitionDetailViewProps {
  competitionId: string;
  onNavigateBack: () => void;
  onTitleChange?: (title: string | null) => void;
}

export const CompetitionDetailView: React.FC<CompetitionDetailViewProps> = ({
  competitionId,
  onNavigateBack,
  onTitleChange,
}) => {
  const [competition, setCompetition] = useState<CompetitionClassRead | null>(null);
  const [results, setResults] = useState<CompetitionResultRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);

  useEffect(() => {
    apiService.getCountries().then(setCountries).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      apiService.getCompetitionDetails(competitionId),
      apiService.getCompetitionResults(competitionId),
    ])
      .then(([comp, res]) => {
        if (cancelled) return;
        setCompetition(comp);
        setResults(res);
        onTitleChange?.('Class Details');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load competition');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [competitionId, onTitleChange]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#132B64]" />
      </div>
    );
  }

  if (error || !competition) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 px-4">
        <AlertTriangle size={48} className="text-red-400" />
        <p className="text-gray-700 text-center">{error || 'Competition not found'}</p>
        <button onClick={onNavigateBack} className="text-sm text-[#132B64] underline">Go back</button>
      </div>
    );
  }

  const flagUrl = competition.unified_location ? getCircleFlagUrl(competition.unified_location) : null;
  const countryName = competition.unified_location ? resolveCountryName(competition.unified_location, countries) : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Competition info banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="max-w-[320px] lg:max-w-[500px] mx-auto">
          {/* Title with flag */}
          <div className="flex items-center gap-3 mb-4">
            {flagUrl && (
              <img
                src={flagUrl}
                alt={countryName || ''}
                className="w-14 h-14 rounded-full shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <h1 className="text-xl font-bold text-[#132B64] leading-tight">
              {competition.unified_class_name || 'Competition'}
            </h1>
          </div>

          {/* Metadata chips */}
          <div className="flex flex-wrap gap-2 mb-2">
            {competition.class_date && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <Calendar size={13} />
                {formatDate(competition.class_date)}
              </span>
            )}
            {countryName && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <MapPin size={13} />
                {countryName}
              </span>
            )}
            {competition.unified_height_cm != null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <Ruler size={13} />
                {competition.unified_height_cm} cm
              </span>
            )}
            {competition.unified_class_num_entries != null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-700">
                <Users size={13} />
                {competition.unified_class_num_entries} entries
              </span>
            )}
            {competition.unified_competition_id && (
              <button
                onClick={() => urlParams.navigateToEventDetail(competition.unified_competition_id!)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-[#132B64] hover:bg-[#0f2350] rounded-full transition-colors"
              >
                <Trophy size={11} />
                View Competition
              </button>
            )}
          </div>

          {/* Source links */}
          {(competition.clipmyhorse_class_url || competition.horsetelex_class_url || competition.equipe_class_url) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-snug mt-1 mb-2">
              <span className="text-gray-500">Sources:</span>
              {competition.clipmyhorse_class_url && (
                <a href={competition.clipmyhorse_class_url} target="_blank" rel="noopener noreferrer"
                   className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                  <img src="/icons/youtube.svg" alt="" className="h-3 w-3" />
                  ClipMyHorse <ExternalLink size={10} />
                </a>
              )}
              {competition.horsetelex_class_url && (
                <a href={competition.horsetelex_class_url} target="_blank" rel="noopener noreferrer"
                   className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                  HorseTelex <ExternalLink size={10} />
                </a>
              )}
              {competition.equipe_class_url && (
                <a href={competition.equipe_class_url} target="_blank" rel="noopener noreferrer"
                   className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                  Equipe <ExternalLink size={10} />
                </a>
              )}
            </div>
          )}

          {/* Stats row */}
          {(competition.unified_class_clear_round_rate != null || competition.unified_class_avg_first_round_faults != null) && (
            <div className="flex gap-4 mt-3 mb-2 text-xs text-gray-500">
              {competition.unified_class_clear_round_rate != null && (
                <span>Clear round rate: <strong className="text-gray-700">{(competition.unified_class_clear_round_rate * 100).toFixed(0)}%</strong></span>
              )}
              {competition.unified_class_avg_first_round_faults != null && (
                <span>Avg faults: <strong className="text-gray-700">{competition.unified_class_avg_first_round_faults.toFixed(1)}</strong></span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Results list */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-[320px] lg:max-w-[500px] mx-auto">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            Results ({results.length})
          </h2>

          {results.length === 0 && !loading && (
            <p className="text-sm text-gray-500 text-center py-8">No results available for this competition.</p>
          )}

          <div className="space-y-3">
            {results.map((result, idx) => {
              const rankStyle = getRankStyle(result.unified_rank);
              const hasFaults = result.unified_first_round_faults != null || result.unified_second_round_faults != null;
              const avatarUrl = getHorseAvatarUrl(result.unified_sex, result.unified_color);
              const isLinkable = !!result.unified_fei_id;
              const Tag = isLinkable ? 'button' : 'div';

              return (
                <Tag
                  key={`${result.unified_horse_key}-${idx}`}
                  {...(isLinkable ? { onClick: () => urlParams.navigateToHorseDetail(result.unified_horse_key) } : {})}
                  className={`w-full bg-[#E7EEE3] rounded-[20px] shadow-sm p-4 sm:p-5 pl-5 sm:pl-7 text-left relative overflow-visible ml-4 sm:ml-6 ${isLinkable ? 'transition-shadow hover:shadow-md cursor-pointer' : 'opacity-75'}`}
                >
                  {/* Horse avatar with rank badge (pop-out) */}
                  <div className="absolute -left-12 sm:-left-14 top-1/2 -translate-y-1/2">
                    <div className="h-16 w-16 rounded-full bg-white border-[3.5px] border-white flex items-center justify-center overflow-hidden shadow-md">
                      <img
                        src={avatarUrl}
                        alt={result.unified_horse_name || 'Horse'}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white ${rankStyle.bg} ${rankStyle.text}`}>
                      {result.unified_rank != null ? result.unified_rank : '–'}
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="text-sm font-bold text-[#132B64] truncate">
                          {formatHorseName(result.unified_horse_name) || 'Unknown Horse'}
                        </h4>
                        {result.unified_rider_name && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{formatPersonName(result.unified_rider_name)}</p>
                        )}
                      </div>

                      {result.unified_prize != null && result.unified_prize > 0 && (
                        <span className="inline-flex items-center gap-1 shrink-0 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                          <Trophy size={11} />
                          {formatPrize(result.unified_prize, result.unified_currency)}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-gray-600">
                      {result.unified_result && (
                        <span className="font-medium">{result.unified_result}</span>
                      )}
                      {hasFaults && (
                        <span>
                          Faults: {result.unified_first_round_faults ?? '–'}
                          {result.unified_second_round_faults != null && ` / ${result.unified_second_round_faults}`}
                        </span>
                      )}
                      {result.unified_time_seconds != null && (
                        <span className="inline-flex items-center gap-0.5">
                          <Clock size={11} />
                          {formatTime(result.unified_time_seconds)}
                        </span>
                      )}
                      {result.unified_status && result.unified_status !== 'normal' && (
                        <span className="text-orange-600 font-medium">{result.unified_status}</span>
                      )}
                    </div>
                  </div>
                </Tag>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
